import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { IonContent, IonFooter, IonPage, IonHeader, IonToolbar, IonButtons, IonBackButton, IonTitle } from '@ionic/react';
import { useParams } from 'react-router-dom';
import { Check, CheckCheck, FileText, Image as ImageIcon, Loader2, Paperclip, Send } from 'lucide-react';
import { SocketEvents } from '@tcrd/shared';
import { Button, cn } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage, newIdempotencyKey } from '../../lib/api';
import { fmtTime } from '../../lib/format';
import { pickFile, pickImage, toForm } from '../../lib/media';
import { realtime } from '../../lib/realtime';
import { useAuth } from '../../stores/auth';
import type { ConversationSummary } from './ChatListPage';

interface Message {
  id: string;
  type: 'TEXT' | 'IMAGE' | 'FILE' | 'PAYMENT_EVIDENCE' | 'SYSTEM';
  body: string | null;
  clientMessageId: string | null;
  createdAt: string;
  mine: boolean;
  sender: { id: string; name: string } | null;
  attachments: Array<{ id: string; mimeType: string; url: string }>;
  pending?: boolean;
  failed?: boolean;
}

interface MessagesPage {
  items: Message[];
  nextCursor: string | null;
  othersLastReadAt: string | null;
}

export function ChatPage({ base }: { base: '/app' | '/driver' }) {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const me = useAuth((s) => s.user);
  const [text, setText] = useState('');
  const [pending, setPending] = useState<Message[]>([]);
  const [typingAt, setTypingAt] = useState(0);
  const [othersReadAt, setOthersReadAt] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const content = useRef<HTMLIonContentElement>(null);
  const lastTypingSent = useRef(0);

  const convs = useQuery({ queryKey: ['conversations'], queryFn: () => api<ConversationSummary[]>('/conversations') });
  const conv = convs.data?.find((c) => c.id === id);
  const q = useInfiniteQuery({
    queryKey: ['messages', id],
    initialPageParam: '',
    queryFn: ({ pageParam }) => api<MessagesPage>(`/conversations/${id}/messages`, { query: { before: pageParam || undefined } }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // Mensajes en orden cronológico (las páginas llegan del más nuevo al más antiguo).
  const messages = useMemo(() => {
    const server = (q.data?.pages ?? []).flatMap((p) => p.items).reverse();
    const ids = new Set(server.map((m) => m.clientMessageId).filter(Boolean));
    return [...server, ...pending.filter((m) => !ids.has(m.clientMessageId))];
  }, [q.data, pending]);
  const readAt = othersReadAt ?? q.data?.pages[0]?.othersLastReadAt ?? null;

  const scrollBottom = () => setTimeout(() => void content.current?.scrollToBottom(200), 50);
  const markRead = () => void api(`/conversations/${id}/read`, { method: 'POST' }).then(() => qc.invalidateQueries({ queryKey: ['conversations'] }));

  useEffect(() => {
    void realtime.emit(SocketEvents.joinConversation, { conversationId: id });
    const offStatus = realtime.onStatus((c) => c && void realtime.emit(SocketEvents.joinConversation, { conversationId: id }));
    const offMsg = realtime.on(SocketEvents.chatMessage, (p) => {
      if (p.conversationId !== id) return;
      void qc.invalidateQueries({ queryKey: ['messages', id] }).then(scrollBottom);
      markRead();
    });
    const offTyping = realtime.on(SocketEvents.chatTyping, (p) => p.conversationId === id && p.userId !== me?.id && setTypingAt(Date.now()));
    const offRead = realtime.on(SocketEvents.chatRead, (p) => p.conversationId === id && p.userId !== me?.id && setOthersReadAt(String(p.at)));
    markRead();
    return () => {
      offStatus();
      offMsg();
      offTyping();
      offRead();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, me?.id]);

  useEffect(() => {
    if (q.isSuccess && !q.isFetchingNextPage) scrollBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.isSuccess]);

  const [, force] = useState(0);
  useEffect(() => {
    if (!typingAt) return;
    const t = setTimeout(() => force((x) => x + 1), 3500);
    return () => clearTimeout(t);
  }, [typingAt]);
  const othersTyping = Date.now() - typingAt < 3500;

  const send = async (retry?: Message) => {
    const body = retry?.body ?? text.trim();
    if (!body) return;
    const clientMessageId = retry?.clientMessageId ?? newIdempotencyKey().replace(/-/g, '').slice(0, 24);
    const optimistic: Message = { id: clientMessageId, type: 'TEXT', body, clientMessageId, createdAt: new Date().toISOString(), mine: true, sender: null, attachments: [], pending: true };
    setPending((x) => [...x.filter((m) => m.clientMessageId !== clientMessageId), optimistic]);
    if (!retry) setText('');
    scrollBottom();
    try {
      await api(`/conversations/${id}/messages`, { body: { body, clientMessageId } });
      await qc.invalidateQueries({ queryKey: ['messages', id] });
      setPending((x) => x.filter((m) => m.clientMessageId !== clientMessageId));
    } catch {
      // Sin conexión: el mensaje queda marcado para reintentar (el id evita duplicados).
      setPending((x) => x.map((m) => (m.clientMessageId === clientMessageId ? { ...m, pending: false, failed: true } : m)));
    }
  };

  const attach = async (kind: 'image' | 'file') => {
    try {
      const blob = kind === 'image' ? await pickImage() : await pickFile('image/jpeg,image/png,image/webp,application/pdf');
      if (!blob) return;
      setUploading(true);
      await api(`/conversations/${id}/attachments`, { method: 'POST', form: toForm(blob, { clientMessageId: newIdempotencyKey().replace(/-/g, '').slice(0, 24) }, 'adjunto') });
      await qc.invalidateQueries({ queryKey: ['messages', id] });
      scrollBottom();
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setUploading(false);
    }
  };

  const onType = (v: string) => {
    setText(v);
    if (Date.now() - lastTypingSent.current > 2500) {
      lastTypingSent.current = Date.now();
      realtime.send(SocketEvents.typing, { conversationId: id });
    }
  };

  const title = conv?.counterpart.map((c) => c.name).join(', ') ?? 'Chat';
  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref={`${base}/chat`} text="" /></IonButtons>
          <IonTitle>
            <span className="block text-base font-bold leading-tight">{title}</span>
            {conv?.request && <span className="block text-xs font-normal text-[var(--text-muted)]">{conv.request.serviceType.name}{conv.request.rides[0] ? ` · ${conv.request.rides[0].code}` : ''}</span>}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={content}>
        <div className="mx-auto max-w-2xl px-3 py-4 flex flex-col gap-1.5">
          {q.hasNextPage && (
            <Button size="sm" variant="ghost" className="self-center" loading={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>Mensajes anteriores</Button>
          )}
          {q.isLoading && <Loader2 className="size-6 animate-spin self-center text-[var(--text-muted)]" aria-label="Cargando" />}
          {messages.length === 0 && q.isSuccess && <p className="text-center text-sm text-[var(--text-muted)] py-10">Escribe para coordinar tu viaje.</p>}
          {messages.map((m) => {
            const seen = m.mine && readAt && new Date(readAt).getTime() >= new Date(m.createdAt).getTime();
            return (
              <div key={m.id} className={cn('max-w-[80%] rounded-2xl px-3.5 py-2 text-[15px]', m.mine ? 'self-end bg-brand-black text-white dark:bg-white dark:text-brand-black rounded-br-md' : 'self-start bg-[var(--surface)] border border-[var(--border)] rounded-bl-md')}>
                {m.attachments.map((a) =>
                  a.mimeType.startsWith('image/') ? (
                    <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer"><img src={a.url} alt="Imagen adjunta" className="rounded-xl mb-1 max-h-72 object-cover" loading="lazy" /></a>
                  ) : (
                    <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 underline mb-1"><FileText className="size-4" aria-hidden /> Documento PDF</a>
                  ),
                )}
                {m.body && <p className="whitespace-pre-wrap break-words" data-no-translate>{m.body}</p>}
                <p className={cn('mt-0.5 text-[11px] flex items-center gap-1 justify-end', m.mine ? 'opacity-70' : 'text-[var(--text-muted)]')}>
                  {fmtTime(m.createdAt)}
                  {m.mine && (m.pending ? <Loader2 className="size-3 animate-spin" aria-label="Enviando" /> : m.failed ? null : seen ? <CheckCheck className="size-3.5 text-sky-400" aria-label="Visto" /> : <Check className="size-3.5" aria-label="Enviado" />)}
                </p>
                {m.failed && <button className="text-xs underline text-brand-red" onClick={() => send(m)}>No enviado · Reintentar</button>}
              </div>
            );
          })}
          {othersTyping && <p className="self-start text-xs text-[var(--text-muted)] px-2">Escribiendo…</p>}
        </div>
      </IonContent>
      <IonFooter className="ion-no-border">
        <div className="mx-auto max-w-2xl flex items-end gap-2 px-3 py-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] bg-[var(--surface-2)]">
          <button className="tap flex items-center justify-center text-[var(--text-muted)]" aria-label="Adjuntar foto" disabled={uploading} onClick={() => attach('image')}>
            {uploading ? <Loader2 className="size-5 animate-spin" /> : <ImageIcon className="size-5" />}
          </button>
          <button className="tap flex items-center justify-center text-[var(--text-muted)]" aria-label="Adjuntar archivo" disabled={uploading} onClick={() => attach('file')}>
            <Paperclip className="size-5" />
          </button>
          <textarea
            value={text}
            onChange={(e) => onType(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            rows={1}
            maxLength={2000}
            placeholder="Mensaje"
            aria-label="Mensaje"
            className="flex-1 resize-none max-h-32 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4 py-3 text-[15px] outline-none focus:ring-2 focus:ring-brand-red/50"
          />
          <button className="tap size-12 rounded-full bg-brand-red text-white flex items-center justify-center disabled:opacity-40" aria-label="Enviar" disabled={!text.trim()} onClick={() => send()}>
            <Send className="size-5" />
          </button>
        </div>
      </IonFooter>
    </IonPage>
  );
}
